import { useEffect, useRef } from 'react';
import {
  INTERNAL_TAG_VALUE,
  StatusFilterType,
  TASK_ACCESS_TAG_TYPE,
} from './loanDetailsConstants';
import { TodoItem, TodoStatus } from './loanDetailsTypes';
import { BORROWER_RESOLUTION, PROPERTY_RESOLUTION } from '@roc/feature-documents';

export const isPendingTodoStatus = (status: TodoStatus) => {
  return [TodoStatus.PENDING, TodoStatus.MORE_INFO_NEEDED].includes(status);
};

export const isPendingOrInReviewTodoStatus = (status: TodoStatus) => {
  return [TodoStatus.PENDING, TodoStatus.MORE_INFO_NEEDED, TodoStatus.BEING_REVIEWED].includes(status);
};

export const isInternalTodo = (todo: TodoItem): boolean =>
  !!todo.document?.internalTask
  || !!todo.taskTags?.some(
    tag => tag.tagType === TASK_ACCESS_TAG_TYPE
      && tag.tagValue?.toUpperCase() === INTERNAL_TAG_VALUE,
  );

export interface FilterTodosByStatusOptions {
  includeParentTasks?: boolean;
}

export const matchesStatusFilter = (
  todo: TodoItem,
  filterBy: string,
  options: FilterTodosByStatusOptions = {},
): boolean => {
  const { includeParentTasks = true } = options;

  if (filterBy === TodoStatus.ALL) {
    return true;
  }

  if (filterBy === TodoStatus.PENDING) {
    return (
      todo.status === filterBy
      || todo.status === TodoStatus.MORE_INFO_NEEDED
      || (includeParentTasks && !!todo.document?.isParentTask)
    );
  }

  if (filterBy === StatusFilterType.INTERNAL_ONLY) {
    return (
      isInternalTodo(todo)
      || (includeParentTasks && !!todo.document?.isParentTask)
    );
  }

  return (
    todo.status === filterBy
    || filterBy.split(',').includes(todo.status)
    || (includeParentTasks && !!todo.document?.isParentTask)
  );
};

export const filterTodosByStatus = (
  todos: TodoItem[],
  filterBy: string,
  options?: FilterTodosByStatusOptions,
): TodoItem[] => {
  return todos.filter(todo => matchesStatusFilter(todo, filterBy, options));
};

export const useIframeMessaging = actions => {
  const actionsRef = useRef<Record<string, (params) => void>>();
  const postMessage = (action, params) => {
    const msg = { action, ...params };
    parent.postMessage(msg, '*');
  };

  actionsRef.current = actions;
  useEffect(() => {
    const handleMessage = event => {
      const data = event.data;
      const actions = actionsRef.current;
      actions[event.data?.action]?.(data);
    };
    addEventListener('message', handleMessage, false);
    return () => removeEventListener('message', handleMessage);
  }, []);

  return { postMessage };
};


export const getTodosWithSubtodos = (todos: TodoItem[]) => {
  let todosWithParentTask = [];
  let todosWithNoParentTask = [];

  todos.forEach(t => {
    if (t.parentTodoId) {
      todosWithParentTask.push(t);
    } else {
      todosWithNoParentTask.push(t);
    }
  });

  let childTodosMap = todosWithParentTask.reduce((acc, current) => {
    const pId = current.parentTodoId;
    const childsSameParent = acc[pId] || []

    return {
      ...acc,
      [pId]: [...childsSameParent, current],
    };
  }, {});

  let newTodosWithSubTodos = todosWithNoParentTask.map(t => {
    if (childTodosMap[t.document?.taskId]) {
      const parentTask = {
        ...t,
        subTodos: childTodosMap[t.document?.taskId],
      }

      childTodosMap[t.document?.taskId] = null;
      return parentTask;
    }

    return t;
  });

  const missingChildGroups = Object.values(childTodosMap).filter(v => !!v);

  if (missingChildGroups) {
    const missingChilds = missingChildGroups.reduce((acc, group) => {
      return [...acc as any, ...group as any];
    }, []);
    newTodosWithSubTodos = [...newTodosWithSubTodos, ...missingChilds as any];
  }

  newTodosWithSubTodos = newTodosWithSubTodos.filter(t => !t.document?.isParentTask || t.subTodos?.length);

  return newTodosWithSubTodos;
}

export const getTodosLengthWithoutParents = (todos: TodoItem[]) => {
  return todos.reduce((sum, current) => {
    if (current.document?.isParentTask) {
      return sum;
    }
    return sum + 1;
  }, 0);
}

const processResolutionItems = (todos: TodoItem[]) => { // TODO: remove this method
  let nonResolutionTodos = [];
  let resolutionTodosMap = {};

  // process parent/child for resolutions
  todos.forEach((current) => {
    if (
      current.parentTodoId
      && current.document?.taskName
      && (current.document?.taskName.includes(BORROWER_RESOLUTION) || current.document?.taskName.includes(PROPERTY_RESOLUTION))
    ) {
      const pId = current.parentTodoId;
      const childsSameParent = resolutionTodosMap[pId] || [];

      resolutionTodosMap[pId] = [...childsSameParent, current];
    } else {
      nonResolutionTodos.push(current);
    }
  }, []);

  if (Object.keys(resolutionTodosMap).length) {
    nonResolutionTodos = nonResolutionTodos.map(t => {
      if (resolutionTodosMap[t.document?.taskId]) {
        const parentTask = {
          ...t,
          subTodos: resolutionTodosMap[t.document?.taskId],
        }

        resolutionTodosMap[t.document?.taskId] = null;
        return parentTask;
      }

      return t;
    });

    const missingChildGroups = Object.values(resolutionTodosMap).filter(v => !!v);

    if (missingChildGroups) {
      const missingChilds = missingChildGroups.reduce((acc, group) => {
        return [...acc as any, ...group as any];
      }, []);
      nonResolutionTodos = [...nonResolutionTodos, ...missingChilds as any];
    }
  }

  return nonResolutionTodos;
}
